import { gql } from '@apollo/client';
import client from 'apollo/client';
import {
  clientPerformanceListFields,
  performanceFindFields,
  performanceHistoryFields,
  performanceTopRankedFields
} from 'common/constants/query-fields/performance';
import {
  getPerformanceFilters,
  getPerformanceHistoryFilters,
  getTopRankedPerformanceFilters
} from 'common/filters/requestFilters';

/**
 * Create performance method
 * @param  {Object} payload
 * @return {Promise}
 */
const create = (payload) =>
  client.mutate({
    mutation: gql`
      mutation createPerformance($input: CreatePerformanceInput!) {
        createPerformance(input: $input) {
          id
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * Find performance method
 * @param  {string} id
 * @return {Promise}
 */
const find = (id) =>
  client.query({
    query: gql`
      query performance($id: ID!) {
        performance(id: $id) {
          ${performanceFindFields}
        }
      }
    `,
    variables: { id },
    fetchPolicy: 'network-only'
  });

/**
 * Get latest performance method
 * @param {Integer} userId
 * @returns {Promise}
 */
const getLatest = (userId) =>
  client.query({
    query: gql`
      query latestPerformance($user_id: Int!) {
        latestPerformance(user_id: $user_id) {
          ${clientPerformanceListFields}
        }
      }
    `,
    variables: { user_id: userId },
    fetchPolicy: 'network-only'
  });

/**
 * Get history method
 * @param {Integer} userId
 * @returns {Promise}
 */
const history = (payload) => {
  const { filters } = payload;
  const performanceFilters = getPerformanceHistoryFilters(filters);

  return client.query({
    query: gql`
      query performanceHistory {
        performanceHistory(
          ${performanceFilters.created_between}
          ${performanceFilters.user_id}
        ) {
          ${performanceHistoryFields}
        }
      }
    `,
    fetchPolicy: 'network-only'
  });
};

/**
 * List performance method
 * @param {Object} payload
 * @returns {Promise}
 */
const list = (payload) => {
  const { fields, filters, pagination } = payload;
  const performanceFilters = getPerformanceFilters(filters);

  return client.query({
    query: gql`
      query performances {
        performances(
          first: ${pagination.first}
          page: ${pagination.page}
          ${performanceFilters.user_id}
          ${performanceFilters.incomplete}
          ${performanceFilters.soft_delete}
          orderBy: {
            column: CREATED_AT,
            order: DESC
          }
        ) {
          paginatorInfo {
            currentPage
            hasMorePages
            total
          }
          data {
            ${fields}
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });
};

/**
 * Remove performance method
 * @param  {Array} ids
 * @return {Promise}
 */
const remove = (ids) =>
  client.mutate({
    mutation: gql`
      mutation deletePerformances($id: [ID!]!) {
        deletePerformances(id: $id) {
          id
        }
      }
    `,
    variables: { id: ids }
  });

/**
 * Restore performance method
 * @param  {Array} ids
 * @return {Promise}
 */
const restore = (ids) =>
  client.mutate({
    mutation: gql`
      mutation restorePerformances($id: [ID!]!) {
        restorePerformances(id: $id) {
          id
        }
      }
    `,
    variables: { id: ids }
  });

/**
 * Top ranked performance method
 *
 * @param {object} filters
 * @returns {Promise}
 */
const topRanked = (filters) => {
  const performanceFilters = getTopRankedPerformanceFilters(filters);

  return client.query({
    query: gql`
      query topRankedPerformances {
        topRankedPerformances(
          ${performanceFilters.created_at}
          ${performanceFilters.performance_test}
        ) {
          ${performanceTopRankedFields}
        }
      }
    `,
    fetchPolicy: 'network-only'
  });
};

/**
 * Update performance method
 * @param  {Object} payload
 * @return {Promise}
 */
const update = (payload) =>
  client.mutate({
    mutation: gql`
      mutation updatePerformance($id: ID!, $input: UpdatePerformanceInput!) {
        updatePerformance(id: $id, input: $input) {
          id
        }
      }
    `,
    variables: {
      id: payload.id,
      input: payload.input
    }
  });

/**
 * Create performance with dynamic exercises
 * @param {Object} payload
 * @return {Promise}
 */
const createWithExercises = (payload) =>
  client.mutate({
    mutation: gql`
      mutation createPerformanceWithExercises($input: CreatePerformanceWithExercisesInput!) {
        createPerformanceWithExercises(input: $input) {
          performance {
            id
            exercises {
              id
              exercise_id
              time_seconds
              repetitions
              weight_kg
              distance_km
              is_personal_record
            }
          }
          new_achievements {
            id
            achievement_type
            localized_name
            value
          }
          new_personal_records {
            id
            exercise_id
            is_personal_record
          }
          streak_updated
          current_streak
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * Create dynamic performance with sets (nuevo sistema)
 * @param {Object} payload
 * @return {Promise}
 */
const createDynamicPerformance = (payload) =>
  client.mutate({
    mutation: gql`
      mutation createDynamicPerformance($input: CreateDynamicPerformanceInput!) {
        createDynamicPerformance(input: $input) {
          id
          client_id
          trainer_id
          exercise_id
          period_start_date
          period_end_date
          observations
          is_dynamic
          exercise {
            id
            name
            parameters {
              id
              parameter_type
              unit
            }
          }
          sets {
            id
            set_number
            time_seconds
            repetitions
            weight_kg
            distance_km
          }
          client {
            id
            first_name
            last_name
            photo
          }
          trainer {
            id
            first_name
            last_name
            photo
          }
          created_at
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * Update dynamic performance with sets
 * @param {string} id
 * @param {Object} input
 * @return {Promise}
 */
const updateDynamicPerformance = (id, input) =>
  client.mutate({
    mutation: gql`
      mutation updateDynamicPerformance($id: ID!, $input: UpdateDynamicPerformanceInput!) {
        updateDynamicPerformance(id: $id, input: $input) {
          id
          period_start_date
          period_end_date
          observations
          sets {
            id
            set_number
            time_seconds
            repetitions
            weight_kg
            distance_km
          }
          created_at
        }
      }
    `,
    variables: { id, input }
  });

/**
 * Delete dynamic performance
 * @param {string} id
 * @return {Promise}
 */
const deleteDynamicPerformance = (id) =>
  client.mutate({
    mutation: gql`
      mutation deleteDynamicPerformance($id: ID!) {
        deleteDynamicPerformance(id: $id) {
          id
        }
      }
    `,
    variables: { id }
  });

/**
 * Get dynamic performances with filters
 * @param {Object} filters
 * @return {Promise}
 */
const getDynamicPerformances = (filters = {}) =>
  client.query({
    query: gql`
      query dynamicPerformances(
        $client_id: ID
        $exercise_id: ID
        $period_start_date: Date
        $period_end_date: Date
      ) {
        dynamicPerformances(
          client_id: $client_id
          exercise_id: $exercise_id
          period_start_date: $period_start_date
          period_end_date: $period_end_date
        ) {
          id
          client_id
          trainer_id
          exercise_id
          period_start_date
          period_end_date
          observations
          is_dynamic
          exercise {
            id
            name
            parameters {
              id
              parameter_type
              unit
            }
          }
          sets {
            id
            set_number
            time_seconds
            repetitions
            weight_kg
            distance_km
          }
          client {
            id
            first_name
            last_name
            photo
          }
          trainer {
            id
            first_name
            last_name
            photo
          }
          created_at
        }
      }
    `,
    variables: filters,
    fetchPolicy: 'network-only'
  });

/**
 * Get single dynamic performance
 * @param {string} id
 * @return {Promise}
 */
const getDynamicPerformance = (id) =>
  client.query({
    query: gql`
      query dynamicPerformance($id: ID!) {
        dynamicPerformance(id: $id) {
          id
          client_id
          trainer_id
          exercise_id
          period_start_date
          period_end_date
          observations
          is_dynamic
          exercise {
            id
            name
            parameters {
              id
              parameter_type
              unit
            }
          }
          sets {
            id
            set_number
            time_seconds
            repetitions
            weight_kg
            distance_km
          }
          client {
            id
            first_name
            last_name
            photo
          }
          trainer {
            id
            first_name
            last_name
            photo
          }
          created_at
        }
      }
    `,
    variables: { id },
    fetchPolicy: 'network-only'
  });

/**
 * Get client dynamic performances grouped by exercise
 * @param {string} clientId
 * @return {Promise}
 */
const getClientDynamicPerformancesByExercise = (clientId) =>
  client.query({
    query: gql`
      query clientDynamicPerformancesByExercise($client_id: ID!) {
        clientDynamicPerformancesByExercise(client_id: $client_id) {
          exercise {
            id
            name
            is_otrixverse
            parameters {
              id
              parameter_type
              unit
            }
          }
          performances {
            id
            period_start_date
            period_end_date
            observations
            sets {
              id
              set_number
              time_seconds
              repetitions
              weight_kg
              distance_km
            }
            trainer {
              id
              first_name
              last_name
              photo
            }
            created_at
          }
          total_count
        }
      }
    `,
    variables: { client_id: clientId },
    fetchPolicy: 'network-only'
  });

export default {
  create,
  createWithExercises,
  createDynamicPerformance,
  updateDynamicPerformance,
  deleteDynamicPerformance,
  getDynamicPerformances,
  getDynamicPerformance,
  getClientDynamicPerformancesByExercise,
  find,
  getLatest,
  history,
  list,
  remove,
  restore,
  topRanked,
  update
};
